import React, { useContext, useState, useEffect } from 'react'
import { Box, Typography, TextField, MenuItem, Select, InputLabel, FormControl, styled } from '@mui/material'
import MobileSkillCard from '../library/cards/MobileSkillCard'
import { useTranslation } from 'react-i18next'
import { SelectedSkillContext } from 'data/index'
import MobileBookSkillCard from './MobileBookSkillCard' 
import { POST } from 'api/index'
import SearchIcon from '@mui/icons-material/Search'

const Wrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100vh',
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0
}));

const ContentContainer = styled(Box)({
  padding: '1rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  width: '100%',
  height: '100%',
  boxSizing: 'border-box',
  paddingTop: 'calc(5rem + 1rem)',
  paddingBottom: 'calc(5rem + 1rem)',
  overflow: 'auto'
});

const SearchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.8rem',
  marginBottom: '0.5rem'
}));

const SearchField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.common.white,
    borderRadius: '0.75rem',
    fontSize: '0.95rem',
    height: '48px',
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
    }
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.95rem',
  }
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.8rem',
  width: '100%'
}));

const FilterControl = styled(FormControl)(({ theme }) => ({
  flex: 1,
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.common.white,
    borderRadius: '0.75rem',
    fontSize: '0.95rem',
    height: '48px',
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
    }
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.95rem',
  }
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 700,
  color: theme.palette.text.primary,
  marginBottom: '0.5rem',
  paddingLeft: '0.2rem'
}));

const CoursesGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0.8rem',
  width: '100%',
  paddingBottom: '0.5rem'
});

const NoResultsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '3rem 1rem',
  textAlign: 'center',
  color: theme.palette.text.secondary
}));

const NoResultsText = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 500,
  marginBottom: '0.5rem'
}));

const NoResultsSubtext = styled(Typography)(({ theme }) => ({
  fontSize: '0.9rem',
  opacity: 0.8
}));

const MobileLibrary = ({ courses }: any) => {
  const { t } = useTranslation();
  const { state: selectedSkill } = useContext(SelectedSkillContext);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [categories, setCategories] = useState<{ id: string; category: string }[]>([]);
  const [courseCategories, setCourseCategories] = useState<{ [key: string]: string[] }>({});
  const [durations, setDurations] = useState<{ [key: number]: number }>({});
  const [coursePoints, setCoursePoints] = useState<{ [key: number]: number }>({});
  const [ratings, setRatings] = useState<{ [key: number]: number }>({});
  const [ratingsCount, setRatingsCount] = useState<{ [key: number]: number }>({});
  const token = window.localStorage.getItem("currentUserToken");

  const generateRandomRatings = () => {
    const storedRatings = localStorage.getItem('courseRatings');
    const storedRatingsCount = localStorage.getItem('courseRatingsCount');
  
    if (storedRatings && storedRatingsCount) {
      setRatings(JSON.parse(storedRatings));
      setRatingsCount(JSON.parse(storedRatingsCount));
      return;
    }
  
    const ratingsMap: { [key: number]: number } = {};
    const ratingsCountMap: { [key: number]: number } = {};
  
    courses.forEach((course: any) => {
      if(course.status < 2){
        const rating = parseFloat((Math.random() * (5.0 - 4.3) + 4.3).toFixed(1));
        const count = Math.floor(Math.random() * (100 - 30 + 1)) + 30;
        ratingsMap[course.id] = rating;
        ratingsCountMap[course.id] = count;
      }
      else{
        ratingsMap[course.id] = 0;
        ratingsCountMap[course.id] = 0;
      }
    });
  
    localStorage.setItem('courseRatings', JSON.stringify(ratingsMap));
    localStorage.setItem('courseRatingsCount', JSON.stringify(ratingsCountMap));
  
    setRatings(ratingsMap);
    setRatingsCount(ratingsCountMap);
  };

  const fetchCategories = async () => {
    try {
      const body = new URLSearchParams();
      if (token) {
        body.append("userToken", token);
      }

      const response = await POST("getCategories", body);
      if (response && response.result) {
        const uniqueCategories = response.result.map((cat: any) => ({
          id: cat.id.toString(),
          category: cat.category,
        }));
        setCategories(uniqueCategories);
      } else {
        console.warn("No categories:", response);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchCourseDurations = async () => {
    try {
      const response = await POST("getAllCourseDurations", new URLSearchParams());
  
      if (response?.result) {
        const durationMap: { [key: number]: number } = {};
  
        response.result.forEach((entry: any) => {
          durationMap[parseInt(entry.course_id)] = parseInt(entry.duration);
        });
  
        setDurations(durationMap);
      }
    } catch (err) {
      console.error("Error fetching durations", err);
    }
  };

  const fetchCoursePoints = async () => {
    try {
      const response = await POST("getAllCoursePoints", new URLSearchParams());
      if (response?.result) {
        const coursePointsMap: { [key: number]: number } = {};
  
        response.result.forEach((entry: any) => {
          coursePointsMap[parseInt(entry.course_id)] = parseInt(entry.total_points);
        });
  
        setCoursePoints(coursePointsMap);
      }
    } catch (err) {
      console.error("Error fetching course points", err);
    }
  };

  const fetchCourseCategories = async () => {
    try {
      const body = new URLSearchParams();
      if (token) {
        body.append("userToken", token);
      }
  
      const response = await POST("getCourseCategories", body);
      if (response && response.result) {
        const categoryMap: { [key: string]: string[] } = {};
  
        response.result.forEach((entry: any) => {
          const { course_id, category_id } = entry;
          if (!categoryMap[course_id]) {
            categoryMap[course_id] = [];
          }
          categoryMap[course_id].push(category_id.toString());
        });
  
        setCourseCategories(categoryMap);
      } else {
        console.warn("No categories fetched:", response);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchCourseCategories();
    fetchCoursePoints();
  }, []);
  
  useEffect(() => {
    if (courses.length > 0) {
      fetchCourseDurations();
      fetchCoursePoints();
      generateRandomRatings();
    }
  }, [courses]);

  const filteredCourses = courses.filter((course: any) => {
    const matchesSearch = course.title.toLowerCase().includes(search.toLowerCase());
    const courseCategoryList = courseCategories[course.id] || [];
    const matchesCategory = category === "All" || courseCategoryList.includes(category);
    return matchesSearch && matchesCategory;
  });

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const handleCategoryChange = (event: any) => {
    setCategory(event.target.value);
  };

  return (
    <>
      <Wrapper>
        <ContentContainer>

          <SearchContainer>
            <SearchField
              variant="outlined"
              size="small"
              fullWidth
              placeholder={t("Search courses...")}
              value={search}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: '#999', mr: 1, fontSize: '1.2rem' }} />,
              }}
            />
            
            <FilterContainer>
              <FilterControl size="small">
                <InputLabel>{t('All Categories')}</InputLabel>
                <Select
                  label={t('All Categories')}
                  value={category}
                  onChange={handleCategoryChange}
                >
                  <MenuItem value="All">{t('All')}</MenuItem>
                  {categories.map(cat => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.category}
                    </MenuItem>
                  ))}
                </Select>
              </FilterControl>
            </FilterContainer>
          </SearchContainer>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <SectionHeader>
              {t('All Courses')} ({filteredCourses.length})
            </SectionHeader>
            
            {filteredCourses.length > 0 ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <CoursesGrid>
                  {filteredCourses.map((course: any, i: number) => (
                    <MobileSkillCard
                      key={course.id || i}
                      data={{
                        ...course,
                        ratings: ratings[course.id] ?? 0,
                        ratingsCount: ratingsCount[course.id] ?? 0,
                        duration: durations[course.id] ?? 0,
                        coursePoints: coursePoints[course.id] ?? 0,
                      }}
                    />
                  ))}
                </CoursesGrid>
              </Box>
            ) : (
              <NoResultsContainer>
                <NoResultsText>
                  {search || category !== 'All' 
                    ? t('No courses found') 
                    : t('No courses available')
                  }
                </NoResultsText>
                <NoResultsSubtext>
                  {search || category !== 'All' 
                    ? t('Try adjusting your search or filter settings')
                    : t('Check back later for new courses')
                  }
                </NoResultsSubtext>
              </NoResultsContainer>
            )}
          </Box>
        </ContentContainer>
      </Wrapper>

      {selectedSkill && <MobileBookSkillCard />}
    </>
  );
};

export default MobileLibrary;